import React, { useContext, useEffect } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Select, { SelectOption } from "@amzn/meridian/select";
import Text from "@amzn/meridian/text";
import Tag from "@amzn/meridian/tag";
import { OutboundFilterContext } from "./context/OutboundFilterContext";
import { FormattedMessage, injectIntl } from "react-intl";
import Link from "@amzn/meridian/link";
import Input from "@amzn/meridian/input";
import {
  getDocumentURL,
  getNodeCapability,
} from "../handler/ParentDataHandler";
import { getBaseUrl, parseCapabilitiesResponse } from "./util/Util";
import { OutboundConfigContext } from "./context/OutboundConfigContext";
import { CapabilityContext } from "../overview/context/CapabilityContext";
import { AR_V2, SSD } from "./util/Constants";
import { useDeviceLayout } from "../common/context/DeviceLayoutContext";
import { FilterExpander } from "../common/FilterExpander";
import { getDeliveryWindowPriority, isSSD } from "../outboundSSD/util/Util";
import Tooltip from "@amzn/meridian/tooltip";
import useOutboundConfigContext from "./context/useOutboundConfigContext";
const OutboundFilter = (props) => {
  const { spacingInset_shorthand2, spacingInset_shorthand4, isMobile } =
    useDeviceLayout();
  const { viewModelF, dispatchF } = useContext(OutboundFilterContext);
  const { outboundConfigModel, configDispatcher } = useContext(
    OutboundConfigContext,
  );
  const { capability } = useContext(CapabilityContext);
  const { outboundConfigModelRef } = useOutboundConfigContext();

  const isXPTFilterRequired = () => {
    const xptNodes = viewModelF.data.xptNodes;
    return (
      !outboundConfigModel.stationConfig.amxl &&
      Array.isArray(xptNodes) &&
      xptNodes.length
    );
  };

  const isFilterSet = () => {
    let result = false;
    Object.entries(viewModelF).forEach((entry) => {
      const key = entry[0];
      const values = entry[1];
      result =
        result ||
        (Array.isArray(values) &&
          values.length &&
          key !== "data" &&
          key !== "selectedPickColumns");
    });
    return result;
  };

  const clearAllFilters = () => {
    dispatchF({ type: "REMOVE_ALL_FILTER" });
  };

  useEffect(() => {
    dispatchF({ type: "LOAD_FILTERS" });

    getDocumentURL().then((documentUrl) => {
      const baseUrl = getBaseUrl(documentUrl);

      configDispatcher({
        type: "GET_STATION_CONFIG",
        data: {
          dispatcher: configDispatcher,
          baseUrl: baseUrl,
        },
      });
    });
  }, []);

  useEffect(() => {
    if (outboundConfigModelRef.current.stationConfig.initialised) {
      Promise.all([getDocumentURL(), getNodeCapability()]).then((result) => {
        const baseUrl = getBaseUrl(result[0]);
        dispatchF({
          type: "LOAD_XPT_NODES",
          data: {
            dispatcher: dispatchF,
            baseUrl: baseUrl,
            capability: parseCapabilitiesResponse(result[1]),
            stationConfig: outboundConfigModelRef.current.stationConfig,
          },
        });
      });
    }
  }, [outboundConfigModelRef.current.stationConfig.initialised]);

  const Filter = () => (
    <Column spacingInset={spacingInset_shorthand4} spacing={"small"}>
      <Column
        spacingInset={spacingInset_shorthand2}
        spacing="small"
        backgroundColor="secondary"
      >
        <Row width="100%" spacingInset="none" spacing="small" wrap="down">
          <Column
            heights="fill"
            alignmentHorizontal="left"
            spacingInset="none small none"
          >
            <Text alignment="left">
              <FormattedMessage
                id="outboundAMZL.filters"
                defaultMessage="Filters:"
              />
            </Text>
          </Column>
          {isXPTFilterRequired() ? (
            <Select
              onChange={(data) =>
                dispatchF({ type: "SELECT_XPT_NODE", xptNodes: data })
              }
              value={viewModelF.xptNodes}
              size="small"
              placeholder={props.intl.formatMessage({
                id: "outboundAMZL.xptNodes",
                defaultMessage: "XPT Nodes",
              })}
              width={"120px"}
            >
              {[...viewModelF.data.xptNodes].sort().map((xptNode) => (
                <SelectOption key={xptNode} label={xptNode} value={xptNode} />
              ))}
            </Select>
          ) : null}
          {SSD !== capability.data ? (
            <Select
              onChange={(data) =>
                dispatchF({ type: "SELECT_DEPARTURE", Departure: data })
              }
              value={viewModelF.departure}
              size="small"
              placeholder={props.intl.formatMessage({
                id: "outboundAMZL.stageByTime",
                defaultMessage: "Stage by time",
              })}
              width={"145px"}
            >
              {[...viewModelF.data.departure].sort().map((departure) => (
                <SelectOption
                  key={departure}
                  label={"Stage by " + departure}
                  value={departure}
                />
              ))}
            </Select>
          ) : null}
          {isSSD(capability.data) &&
          outboundConfigModel.stationConfig.tabularGridViewSSDEnabled ? (
            <Select
              onChange={(data) =>
                dispatchF({ type: "SELECT_WINDOW", DeliveryWindow: data })
              }
              value={viewModelF.deliveryWindow}
              size="small"
              placeholder={props.intl.formatMessage({
                id: "outboundSSD.window",
                defaultMessage: "Window",
              })}
              width={"145px"}
            >
              {[...viewModelF.data.deliveryWindow]
                .sort((a, b) => {
                  const deliveryWindowPriorityA = getDeliveryWindowPriority(a);
                  const deliveryWindowPriorityB = getDeliveryWindowPriority(b);
                  return deliveryWindowPriorityA - deliveryWindowPriorityB;
                })
                .map((deliveryWindow) => (
                  <SelectOption
                    key={deliveryWindow}
                    label={deliveryWindow}
                    value={deliveryWindow}
                  />
                ))}
            </Select>
          ) : null}
          {AR_V2 !== capability.data &&
          !(
            isSSD(capability.data) &&
            outboundConfigModel.stationConfig.tabularGridViewSSDEnabled
          ) ? (
            <Select
              onChange={(data) =>
                dispatchF({ type: "SELECT_DRIVER_TYPE", DriverType: data })
              }
              value={viewModelF.driverType}
              size="small"
              placeholder={
                outboundConfigModel.stationConfig.amxl
                  ? props.intl.formatMessage({
                      id: "outboundAMXL.routeType",
                      defaultMessage: "Route Type",
                    })
                  : props.intl.formatMessage({
                      id: "outboundAMZL.driverType",
                      defaultMessage: "Driver Type",
                    })
              }
              width={"125px"}
            >
              {[...viewModelF.data.driverType].sort().map((driverType) => (
                <SelectOption
                  key={driverType}
                  label={driverType}
                  value={driverType}
                />
              ))}
            </Select>
          ) : null}
          {!outboundConfigModel.stationConfig.amxl &&
          AR_V2 !== capability.data &&
          !(
            isSSD(capability.data) &&
            outboundConfigModel.stationConfig.tabularGridViewSSDEnabled
          ) ? (
            <Select
              onChange={(data) =>
                dispatchF({ type: "SELECT_CLUSTER", Cluster: data })
              }
              value={viewModelF.cluster}
              size="small"
              placeholder={props.intl.formatMessage({
                id: "outboundAMZL.cluster",
                defaultMessage: "Cluster",
              })}
              width={"115px"}
            >
              {[...viewModelF.data.cluster].sort().map((cluster) => (
                <SelectOption key={cluster} label={cluster} value={cluster} />
              ))}
            </Select>
          ) : null}
          {outboundConfigModel.stationConfig.ssdmmotenabled ? (
            <Select
              onChange={(data) =>
                dispatchF({ type: "SELECT_ANCHOR_POINT", sourceNode: data })
              }
              value={viewModelF.sourceNode}
              size="small"
              placeholder={props.intl.formatMessage({
                id: "outboundSSD.sourceNode",
                defaultMessage: "Anchor Points",
              })}
              width={"155px"}
            >
              {[...viewModelF.data.sourceNode].sort().map((sourceNode) => (
                <SelectOption
                  key={sourceNode}
                  label={sourceNode}
                  value={sourceNode}
                />
              ))}
            </Select>
          ) : null}
          {outboundConfigModel.stationConfig.amxl ? (
            <Tooltip
              position="end"
              title="Shipment Type filter is currently not supported."
            >
              <Select
                onChange={(data) =>
                  dispatchF({
                    type: "SELECT_SHIPMENT_TYPE",
                    ShipmentType: data,
                  })
                }
                value={viewModelF.shipmentType}
                size="small"
                placeholder={props.intl.formatMessage({
                  id: "outboundAMXL.shipmentType",
                  defaultMessage: "Shipment Type",
                })}
                width={"115px"}
                disabled={true}
              >
                {[...viewModelF.data.shipmentType]
                  .sort()
                  .map((shipmentType) => (
                    <SelectOption
                      key={shipmentType}
                      label={shipmentType}
                      value={shipmentType}
                    />
                  ))}
              </Select>
            </Tooltip>
          ) : null}

          {!outboundConfigModel.stationConfig.amxl &&
          isSSD(capability.data) &&
          outboundConfigModel.stationConfig.tabularGridViewSSDEnabled ? (
            <Row
              alignmentHorizontal={"right"}
              spacingInset={"none none none medium"}
              spacing={"small"}
            >
              <Text>
                <FormattedMessage
                  id="outboundSSD.cptThresholds"
                  defaultMessage="CPT Thresholds"
                />
                :
              </Text>
              <Input
                type="number"
                size="small"
                width="105px"
                placeholder={props.intl.formatMessage({
                  id: "outboundSSD.time",
                  defaultMessage: "Time",
                })}
                value={viewModelF.cptTimeLimit}
                onChange={(data) =>
                  dispatchF({
                    type: "SELECT_CPT_TIME_LIMIT",
                    cptTimeLimit: data,
                  })
                }
                suffix={"mins"}
              />
            </Row>
          ) : null}
          {!outboundConfigModel.stationConfig.amxl &&
          AR_V2 !== capability.data &&
          !(
            isSSD(capability.data) &&
            outboundConfigModel.stationConfig.tabularGridViewSSDEnabled
          ) ? (
            <Row
              alignmentHorizontal={"right"}
              spacingInset={"none none none medium"}
              spacing={"small"}
            >
              <Text>
                <FormattedMessage
                  id="pick.sprThresholds"
                  defaultMessage="SPR thresholds"
                />
                :
              </Text>
              <Input
                type="number"
                size="small"
                width="105px"
                placeholder={props.intl.formatMessage({
                  id: "pick.time",
                  defaultMessage: "Time",
                })}
                value={viewModelF.sprTimeLimit}
                onChange={(data) =>
                  dispatchF({
                    type: "SELECT_SPR_TIME_LIMIT",
                    sprTimeLimit: data,
                  })
                }
                suffix={"mins"}
              />
              <Input
                type="number"
                size="small"
                width="60px"
                value={viewModelF.sprPackageCount}
                onChange={(data) =>
                  dispatchF({
                    type: "SELECT_SPR_PACKAGE_COUNT",
                    sprPackageCount: data,
                  })
                }
                placeholder={props.intl.formatMessage({
                  id: "pick.spr",
                  defaultMessage: "SPR",
                })}
              />
            </Row>
          ) : null}
          {/* Route Cut Filter */}
          {AR_V2 === capability.data ? (
            <Row
              alignmentHorizontal={"right"}
              spacingInset={"none none none medium"}
              spacing={"small"}
            >
              <Text>
                <FormattedMessage
                  id="rc.lastPickThreshold"
                  defaultMessage="Last pick"
                />
                :
              </Text>
              <Input
                type="number"
                size="small"
                width="105px"
                placeholder={props.intl.formatMessage({
                  id: "rc.time",
                  defaultMessage: "0",
                })}
                value={viewModelF.rcTimeLimit}
                onChange={(data) =>
                  dispatchF({ type: "SELECT_RC_TIME_LIMIT", rcTimeLimit: data })
                }
                suffix={"mins"}
              />
              <Text>
                <FormattedMessage
                  id="rc.packageThreshold"
                  defaultMessage="Packages cut"
                />
                :
              </Text>
              <Input
                type="number"
                size="small"
                width="105px"
                value={viewModelF.rcPackageCount}
                onChange={(data) =>
                  dispatchF({
                    type: "SELECT_RC_PACKAGE_COUNT",
                    rcPackageCount: data,
                  })
                }
                placeholder={props.intl.formatMessage({
                  id: "rc.pkg",
                  defaultMessage: "0",
                })}
                suffix={"pkgs"}
              />
            </Row>
          ) : null}
        </Row>
      </Column>
      <Row width="100%" spacingInset="none" spacing="small">
        <div className="filter-tag">
          {Object.entries(viewModelF).map((entry) => {
            const key = entry[0];
            const values = entry[1];
            return Array.isArray(values) && key !== "data"
              ? values.map((value) => {
                  return (
                    <Tag
                      key={value}
                      type="theme"
                      onClose={() =>
                        dispatchF({
                          type: "REMOVE_FILTER",
                          data: { key, value },
                        })
                      }
                    >
                      {value}
                    </Tag>
                  );
                })
              : null;
          })}
        </div>
        {isFilterSet() ? (
          <Link onClick={clearAllFilters} type="secondary">
            Clear all
          </Link>
        ) : null}
      </Row>
    </Column>
  );

  return (
    <React.Fragment>
      {isMobile ? (
        <FilterExpander>
          <Filter />
        </FilterExpander>
      ) : (
        <Filter />
      )}
    </React.Fragment>
  );
};

export default injectIntl(OutboundFilter);



// WEBPACK FOOTER //
// ./src/components/outboundAmzl/OutboundFilter.js